import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { GlobalConstants } from '@globalConstants';
import { IUser } from '@interfaces/IUser';
import { IAppState } from '@myStore/app.state';
import { SendStudentDropoutFromGroupRequest } from '@myStore/groups-changes/groups-changes.actions';
import {
  ClearNoteAboutStudentErrors,
  SendBlockStudentRequest,
  SendNoteAboutStudentRequest,
} from '@myStore/users-changes/users-changes.actions';
import { selectUsersChangesSuccess } from '@myStore/users-changes/users-changes.selector';
import { select, Store } from '@ngrx/store';
import { FormDialogComponent } from '@shared/components/UI/form-dialog/form-dialog.component';
import { isControlsIsValid } from '@shared/utils/formUtils';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ILeadData } from '@interfaces/ISales';
import {
  ChangeLeadDataRequest,
  ClearChangeLeadDataErrors,
} from '@myStore/sales-changes/sales-changes.actions';
import { GetLeadRequest } from '@myStore/sales-view/sales-view.actions';

@Component({
  selector: 'app-student-note-form',
  templateUrl: './student-note-form.component.html',
  styleUrls: ['./student-note-form.component.sass'],
})
export class StudentNoteFormComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();

  public student?: IUser;
  public type?: string;
  public isEdit?: boolean;

  readonly studentStatus = GlobalConstants.StudentStatus;
  readonly noteType = GlobalConstants.StudentsNoteTypes;

  deductionForm = new UntypedFormGroup({
    student: new UntypedFormControl(''),
    type: new UntypedFormControl(''),
    text: new UntypedFormControl('', [Validators.required]),
    group: new UntypedFormControl(0),
  });

  sendLoading$!: Observable<boolean>;
  groupId?: number;
  studentListStatus?: string;
  lead?: ILeadData;

  constructor(
    private _store: Store<IAppState>,
    public dialogRef: MatDialogRef<FormDialogComponent>
  ) {}

  ngOnInit(): void {
    const dialogData = this.dialogRef.componentInstance.data;
    this.student = dialogData.student;
    this.groupId = dialogData.groupId;
    this.type = dialogData.type;
    this.lead = dialogData.lead;
    this.isEdit = dialogData.isEdit || false;
    this.studentListStatus = dialogData.studentListStatus;
    this.sendLoading$ = dialogData.loading$;
    this.deductionForm.patchValue({
      student: dialogData.student?.id,
      type: dialogData.type,
      group: dialogData.groupId,
    });

    this._store
      .pipe(select(selectUsersChangesSuccess), takeUntil(this.destroy$))
      .subscribe(success => {
        if (success) {
          if (this.type === this.noteType.ESDP) {
            this.onCancel();
            this._store.dispatch(new ClearNoteAboutStudentErrors());
          }
          this._store.dispatch(new ClearChangeLeadDataErrors());
        }
      });
    if (this.lead && this.isEdit) {
      this.deductionForm.patchValue({
        text: this.lead.general_comment,
      });
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSubmit() {
    if (!this.lead) {
      const data = this.deductionForm.value;

      if (isControlsIsValid(this.deductionForm.controls)) {
        this._store.dispatch(new SendNoteAboutStudentRequest(data));

        if (
          this.studentListStatus === this.studentStatus.active &&
          this.type === this.noteType.EXPULSION
        ) {
          if (this.groupId && this.student && this.student.id) {
            this._store.dispatch(
              new SendStudentDropoutFromGroupRequest(this.groupId, [
                this.student.id,
              ])
            );
          }
        }

        if (
          this.studentListStatus === this.studentStatus.inactive &&
          this.type === this.noteType.BLOCKING
        ) {
          if (this.groupId && this.student && this.student.id) {
            this._store.dispatch(new SendBlockStudentRequest(this.student.id));
          }
        }
      }
    } else {
      if (this.deductionForm.value.text) {
        const data = {
          phone_number: this.lead.phone_number,
          email: this.lead.email,
          general_comment: this.deductionForm.value.text,
        };
        this._store.dispatch(new ChangeLeadDataRequest(data, this.lead.id));
        this._store.dispatch(new GetLeadRequest(Number(this.lead.id)));
      }
    }
  }

  ngOnDestroy() {
    this._store.dispatch(new ClearNoteAboutStudentErrors());
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
